/**
 * Empty State Component
 * Consistent empty state display with icon, message, and action
 */

import React from 'react';
import { Empty, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { spacing, typography, colors } from '../../theme/designTokens';

/**
 * EmptyState Component
 *
 * @param {string} icon - Ant Design icon component
 * @param {string} title - Empty state title
 * @param {string} description - Empty state description
 * @param {string} actionText - CTA button text
 * @param {function} onAction - CTA button click handler
 * @param {ReactNode} image - Custom illustration/image
 * @param {object} style - Additional styles
 */
function EmptyState({
  icon,
  title = 'No Data',
  description,
  actionText,
  onAction,
  image,
  style = {},
}) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: `${spacing.xxl}px ${spacing.lg}px`,
        ...style,
      }}
    >
      {/* Custom image or default Empty */}
      {image || icon ? (
        <div style={{ marginBottom: spacing.lg }}>
          {image || (
            <div
              style={{
                fontSize: 64,
                color: colors.neutral[400],
                marginBottom: spacing.md,
              }}
            >
              {icon}
            </div>
          )}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginBottom: spacing.lg }}
        />
      )}

      {/* Title */}
      <h3
        style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          color: colors.text.primary,
          margin: `0 0 ${spacing.xs}px 0`,
        }}
      >
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p
          style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.secondary,
            margin: `0 0 ${spacing.lg}px 0`,
            maxWidth: 400,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {description}
        </p>
      )}

      {/* Action button */}
      {actionText && onAction && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAction}
          size="large"
        >
          {actionText}
        </Button>
      )}
    </div>
  );
}

export default EmptyState;
